// frontend/src/components/InitScreen.tsx
// Pantalla de inicialización — estilo institucional Stitch

import { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { storage } from '../services/storage';
import { AlertCircle, CheckCircle2, Database, Download, FileText, RefreshCw } from 'lucide-react';

interface InitStatus {
  inicializado: boolean;
  totalCadetes: number;
  mensaje: string;
}

export function InitScreen({ onInicializado }: { onInicializado: () => void }) {
  const [status, setStatus] = useState<InitStatus | null>(null);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [importando, setImportando] = useState(false);
  const [resultado, setResultado] = useState<{ total: number; exitosos: number; fallidos: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { checkStatus(); }, []);

  const checkStatus = async () => {
    setCheckingStatus(true);
    setError(null);
    try {
      const response = await api.getInitStatus();
      setStatus(response.data);
      if (response.data.inicializado) {
        const cadetesLocales = await storage.getCadetes();
        if (cadetesLocales.length < response.data.totalCadetes) {
          await sincronizarDesdeServidor();
        } else {
          onInicializado();
        }
      }
    } catch (err: any) {
      setStatus(null);
      setError(
        err?.message === 'NETWORK_ERROR'
          ? 'No se puede conectar con el servidor. Verifica que el backend esté iniciado en el puerto 3001.'
          : 'Error al verificar estado del sistema.'
      );
    } finally {
      setCheckingStatus(false);
    }
  };

  const sincronizarDesdeServidor = async () => {
    try {
      setImportando(true);
      const response = await api.getCadetes();
      if (response.success && response.data) {
        await storage.setCadetes(response.data);
        onInicializado();
      }
    } catch {
      setError('Error al sincronizar desde el servidor.');
    } finally {
      setImportando(false);
    }
  };

  const handleImportar = async () => {
    setImportando(true);
    setError(null);
    setResultado(null);
    try {
      const response = await api.importarCadetes();
      if (response.success) {
        setResultado(response.data);
        await sincronizarDesdeServidor();
      }
    } catch (err: any) {
      setError(err.message || 'Error al importar cadetes.');
    } finally {
      setImportando(false);
    }
  };

  const isBusy = importando || checkingStatus;

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center p-6 animate-fade-in"
      style={{ backgroundColor: 'var(--color-sgcc-surface)', fontFamily: 'Inter, sans-serif' }}
    >
      <div className="w-full max-w-sm">
        {/* Header */}
        <div className="flex flex-col items-center mb-8">
          <div
            className="w-20 h-20 rounded-full flex items-center justify-center border-2 mb-4"
            style={{
              backgroundColor: 'var(--color-sgcc-primary-container)',
              borderColor: 'var(--color-sgcc-on-primary-container)',
            }}
          >
            {isBusy ? (
              <RefreshCw size={40} strokeWidth={1.6} className="animate-spin-slow" style={{ color: 'var(--color-sgcc-on-primary-container)' }} />
            ) : (
              <Database size={40} strokeWidth={1.6} style={{ color: 'var(--color-sgcc-on-primary-container)' }} />
            )}
          </div>
          <h1 className="font-bold text-2xl uppercase tracking-tight" style={{ color: 'var(--color-sgcc-primary)' }}>
            SGCC
          </h1>
          <p className="text-xs uppercase tracking-widest mt-1" style={{ color: 'var(--color-sgcc-outline)' }}>
            Inicialización del Sistema
          </p>
        </div>

        {/* Error */}
        {error && (
          <div
            className="flex items-start gap-2.5 p-3 rounded-lg border mb-4 text-sm animate-fade-in"
            style={{
              backgroundColor: 'var(--color-sgcc-error-container)',
              borderColor: 'rgba(186,26,26,0.3)',
              color: 'var(--color-sgcc-on-error-container)',
            }}
          >
            <AlertCircle size={18} strokeWidth={2} className="shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {/* Success result */}
        {resultado && (
          <div
            className="flex items-start gap-2.5 p-3 rounded-lg border mb-4 text-sm animate-fade-in"
            style={{
              backgroundColor: 'var(--color-status-present-bg)',
              borderColor: 'rgba(16,185,129,0.3)',
              color: 'var(--color-status-present)',
            }}
          >
            <CheckCircle2 size={18} strokeWidth={2} className="shrink-0 mt-0.5" />
            <span>
              {resultado.exitosos} de {resultado.total} cadetes importados
              {resultado.fallidos > 0 && ` (${resultado.fallidos} con errores)`}
            </span>
          </div>
        )}

        {/* Info card */}
        <div
          className="rounded-xl border p-4 mb-6"
          style={{
            backgroundColor: 'var(--color-sgcc-surface-container-lowest)',
            borderColor: 'var(--color-sgcc-outline-variant)',
          }}
        >
          {checkingStatus ? (
            <div className="space-y-2">
              <div className="skeleton h-4 w-3/4 rounded" />
              <div className="skeleton h-3 w-full rounded" />
              <div className="skeleton h-3 w-2/3 rounded" />
            </div>
          ) : status ? (
            <div>
              <p className="text-sm font-semibold mb-1" style={{ color: 'var(--color-sgcc-on-surface)' }}>
                Importar Nómina de Cadetes
              </p>
              <p className="text-xs" style={{ color: 'var(--color-sgcc-outline)' }}>
                El sistema importará los cadetes desde el archivo CSV del servidor.
              </p>
              <div
                className="mt-3 flex items-center gap-2 px-3 py-2 rounded-lg"
                style={{ backgroundColor: 'var(--color-sgcc-surface-container)' }}
              >
                <FileText size={16} strokeWidth={1.8} style={{ color: 'var(--color-sgcc-outline)' }} />
                <span className="text-[11px]" style={{ color: 'var(--color-sgcc-outline)' }}>
                  cadetes.csv
                </span>
              </div>
            </div>
          ) : (
            <div>
              <p className="text-sm font-semibold mb-1" style={{ color: 'var(--color-sgcc-on-surface)' }}>
                Sin conexión al servidor
              </p>
              <p className="text-xs" style={{ color: 'var(--color-sgcc-outline)' }}>
                No se pudo obtener el estado inicial. Verifica que el backend esté activo e intenta nuevamente.
              </p>
            </div>
          )}
        </div>

        {/* Action button */}
        <button
          id="init-action-btn"
          className="w-full h-12 rounded-xl font-semibold text-sm uppercase tracking-wider tap-active flex items-center justify-center gap-2"
          style={{
            backgroundColor: isBusy ? 'var(--color-sgcc-outline-variant)' : 'var(--color-sgcc-primary)',
            color: 'white',
            cursor: isBusy ? 'not-allowed' : 'pointer',
          }}
          onClick={status ? handleImportar : checkStatus}
          disabled={isBusy}
        >
          {isBusy ? (
            <>
              <RefreshCw size={20} strokeWidth={2} className="animate-spin-slow" />
              <span>{importando ? 'Importando...' : 'Verificando...'}</span>
            </>
          ) : (
            <>
              {status ? <Download size={20} strokeWidth={2} /> : <RefreshCw size={20} strokeWidth={2} />}
              <span>{status ? 'Importar Cadetes' : 'Reintentar conexión'}</span>
            </>
          )}
        </button>

        <p className="text-center text-[11px] mt-4" style={{ color: 'var(--color-sgcc-outline)' }}>
          Este proceso puede tardar unos segundos.
        </p>
      </div>
    </div>
  );
}
